import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

export type ErrorType = 'auth' | 'network' | 'validation' | 'server' | 'general';

export interface ErrorNotification {
  id: string;
  type: ErrorType;
  title: string;
  message: string;
  isRetryable: boolean;
  autoRemove?: boolean;
  duration?: number;
  onRetry?: () => void;
  onDismiss?: () => void;
}

interface ErrorNotificationContextType {
  notifications: ErrorNotification[];
  showError: (error: Omit<ErrorNotification, 'id'>) => string;
  showAuthError: (message: string, onRetry?: () => void) => string;
  dismissError: (id: string) => void;
  clearAll: () => void;
}

const ErrorNotificationContext = createContext<ErrorNotificationContextType | undefined>(undefined);

interface ErrorNotificationProviderProps {
  children: ReactNode;
}

export function ErrorNotificationProvider({ children }: ErrorNotificationProviderProps) {
  const [notifications, setNotifications] = useState<ErrorNotification[]>([]);

  const generateId = () => `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const dismissError = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const showError = useCallback((errorData: Omit<ErrorNotification, 'id'>): string => {
    const id = generateId();
    const notification: ErrorNotification = {
      id,
      autoRemove: true,
      duration: 8000,
      ...errorData,
    };

    setNotifications(prev => [...prev, notification]);

    // Auto-remove after duration if specified
    if (notification.autoRemove && notification.duration) {
      setTimeout(() => {
        dismissError(id);
      }, notification.duration);
    }

    return id;
  }, [dismissError]);

  const showAuthError = useCallback((message: string, onRetry?: () => void): string => {
    return showError({
      type: 'auth',
      title: 'Authentication Required',
      message,
      isRetryable: false,
      autoRemove: false, // Keep auth errors visible until dismissed
      onRetry,
    });
  }, [showError]);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const contextValue: ErrorNotificationContextType = {
    notifications,
    showError,
    showAuthError,
    dismissError,
    clearAll,
  };

  return (
    <ErrorNotificationContext.Provider value={contextValue}>
      {children}
    </ErrorNotificationContext.Provider>
  );
}

export function useErrorNotification(): ErrorNotificationContextType {
  const context = useContext(ErrorNotificationContext);
  if (context === undefined) {
    throw new Error('useErrorNotification must be used within an ErrorNotificationProvider');
  }
  return context;
}

// Utility hook for common error patterns
export function useAuthErrorHandler() {
  const { showAuthError } = useErrorNotification();

  const handleTokenExpired = useCallback(() => {
    return showAuthError(
      'Your session has expired. Please log in again to continue.',
      () => {
        window.location.href = '/admin/login';
      }
    );
  }, [showAuthError]);

  const handleAuthRequired = useCallback(() => {
    return showAuthError(
      'Authentication is required to perform this action. Please log in.',
      () => {
        window.location.href = '/admin/login';
      }
    );
  }, [showAuthError]);

  const handlePermissionDenied = useCallback(() => {
    return showAuthError(
      'You do not have permission to perform this action.'
    );
  }, [showAuthError]);

  return {
    handleTokenExpired,
    handleAuthRequired,
    handlePermissionDenied,
  };
}
